import { BaseIcon, Button, Modal, ModalProps } from "@shltelemedicine/shl-ui-lib";
import { useEffect, useState } from "react";
import Form from "react-bootstrap/Form";
import Spinner from "react-bootstrap/Spinner";
import { useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import sendIcon from "../../assets/buttons/send_mail.svg";
import supportIcon from "../../assets/support/support_modal.svg";
import { emailPattern } from "../../constants";
import useAlert from "../../hooks/useAlert";
import { IGetSupport } from "../../models/support.interface";
import usersService from "../../services/users.service";
import AlertModal from "../Shared/AlertModal/AlertModal";
import CancelButton from "../Shared/CancelButton/CancelButton";
import TextFormInput from "../Shared/TextInput/TextFormInput";
import styles from "./SupportModal.module.css";

type SupportModalProps = {
  email?: string;
  subject?: string;
  description?: string;
} & ModalProps;

const SupportModal = ({
  show,
  email,
  onClose,
  subject,
  description,
}: SupportModalProps) => {
  const intl = useIntl();
  const [loading, setLoading] = useState(false);

  const defaultValues = {
    sender: email,
    subject,
    description,
  };

  const {
    reset,
    handleSubmit,
    control,
    formState: { errors: formErrors },
  } = useForm<IGetSupport>({ defaultValues });

  const { showErrorAlert, alertProps, alertMessage } = useAlert();

  useEffect(() => {
    if (show) {
      reset({ subject, description, sender: email });
    }
  }, [show, reset, subject, description, email]);

  const sendHandler = async (formData: IGetSupport) => {
    try {
      setLoading(true);
      const isSuccess = await usersService.getSupport(formData);
      if (!isSuccess) {
        showErrorAlert(
          <FormattedMessage
            id="ERROR.CONTACT_SUPPORT"
            defaultMessage="An error occurred while trying to send a message to Customer Service.<br></br>Please try again."
            values={{
              br: () => <br></br>,
            }}
          />,
          { showLink: false }
        );
        return;
      }
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const textPlaceholder = intl.messages["GLOBAL.INSERT_HERE"] as string;

  return (
    <>
      <Modal
        centered
        backdrop="static"
        show={show}
        onHide={onClose}
        contentClassName="h-100"
        header={
          <div className="d-flex align-items-center">
            <BaseIcon icon={supportIcon} className={styles.headerIcon} />
            <span className="mx-1"></span>
            <p className={`${styles.title} mb-0`}>
              <FormattedMessage
                id="SUPPORT.TITLE"
                defaultMessage="Contact Customer Service"
              />
            </p>
          </div>
        }
        footer={
          <div className="d-flex align-items-center justify-content-end border-top pt-2 px-3 m-sm-0">
            <CancelButton onClick={onClose} disabled={loading} />
            <span className="mx-2"></span>
            <Button
              bgColor="transparent linear-gradient(180deg, #505F95 0%, #2C449A 100%) 0% 0% no-repeat padding-box"
              icon={{ src: sendIcon, width: 28, height: 18 }}
              disabled={loading}
              onClick={handleSubmit(sendHandler)}
            >
              <span className="capitalized">
                <FormattedMessage id="GLOBAL.SEND" defaultMessage="Send" />
              </span>
              {loading && (
                <>
                  &nbsp;&nbsp;
                  <Spinner as="span" animation="border" size="sm" />
                </>
              )}
            </Button>
          </div>
        }
      >
        <div className="mx-4 font-md">
          <p className={`${styles.subTitle} px-1 mb-3`}>
            <FormattedMessage
              id="SUPPORT.SUBTITLE"
              defaultMessage="Please contact us with any questions you might have"
            />
          </p>
          <Form>
            <Form.Group className="mb-2">
              <Form.Label className="mb-1 font-md d-flex align-items-center">
                <span className="prim-color align-middle">*</span>&nbsp;
                <FormattedMessage
                  id="SUPPORT.SUBJECT"
                  defaultMessage="Subject"
                />
                <span className="mx-1"></span>
              </Form.Label>

              <TextFormInput
                name={"subject"}
                control={control}
                rules={{
                  required: true,
                }}
                input={{
                  isInvalid: !!formErrors?.subject,
                  placeholder: textPlaceholder,
                }}
              />
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Label className="mb-1 font-md d-flex align-items-center">
                <span className="prim-color align-middle">*</span>&nbsp;
                <FormattedMessage
                  id="SUPPORT.DESCRIPTION"
                  defaultMessage="Description"
                />
                <span className="mx-1"></span>
              </Form.Label>

              <TextFormInput
                name={"description"}
                control={control}
                rules={{
                  required: true,
                }}
                input={{
                  isInvalid: !!formErrors?.description,
                  type: "textarea",
                  textareaRows: 8,
                  placeholder: textPlaceholder,
                }}
              />
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Label className="mb-1 font-md d-flex align-items-center">
                <span className="prim-color align-middle">*</span>&nbsp;
                <FormattedMessage
                  id="SUPPORT.REPLY_TO"
                  defaultMessage="Reply to me at"
                />
                <span className="mx-1"></span>
              </Form.Label>

              <TextFormInput
                name={"sender"}
                control={control}
                rules={{
                  required: true,
                  pattern: emailPattern,
                }}
                input={{
                  isInvalid: !!formErrors?.sender,
                  placeholder: "name.example@email.com",
                }}
              />
            </Form.Group>
          </Form>
        </div>
      </Modal>
      <AlertModal {...alertProps}>{alertMessage}</AlertModal>
    </>
  );
};

export default SupportModal;
